import React, { useState } from 'react';

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [showDUAModal, setShowDUAModal] = useState(false);
  
  // Toggle FAQ answer visibility
  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // FAQ data
  const faqs = [
    {
      question: "What types of data are available in the portal?",
      answer: "Our portal contains multiple myeloma genomic datasets including RNA-seq, WES, WGS, and single-cell sequencing data. Each dataset has been collected from patients at various stages of disease. We intend to share our datasets here as we work on our publications. Our main focus will be on providing user friendly tools and results for end users."
    },
    {
      question: "How should I cite data from this portal?",
      answer: "All data used from this portal should be properly cited in publications. Each dataset presented in the Search section shows required citations. Please visit our Citations page for dataset specific citations and for our portal. Generally, you should cite both the original publication and acknowledge the DFCI MM Data Access Portal."
    },
    {
      question: "Can I download raw data files?",
      answer: "Raw data files are available for approved researchers who have completed the Data Use Agreement (DUA). After your access request is approved and DUA is in place, we will be able to share raw data files through a secure HIPAA compliant cloud storage."
    },
    {
      question: "How do I request a data access?",
      answer: (
        <div>
          <p>You must adhere to the DUA agreement with us. It requires each Principal Investigator (PI) to apply for data access and use the data solely for the specific project approved in the DUA. Commercial use and intellectual property applications are not permitted.</p>
          <button 
            className="link-button" 
            onClick={() => setShowDUAModal(true)}
          >
            Click here to open the application form
          </button>
        </div>
      )
    },
    {
      question: "How often is the data updated?",
      answer: "There is no fixed schedule for dataset updates. While we make every effort to maintain the datasets and update the analysis results, we do not have a specific timeframe for these updates."
    },
    {
      question: "How do you analyze datasets?",
      answer: "It is always good to check the original publications; however, the pipelines and tools we use change over time. We provide detailed information for each dataset when you make a query."
    },
    {
      question: "Can I make a request for specific tools or plots to be presented on this portal?",
      answer: "We encourage our users to reach out to us for specific figure or analysis requests. Please check the 'Contact' page for more details. While we cannot guarantee that all requests will be addressed immediately, we will do our best to fulfill them."
    },
    {
      question: "I’m working in an industry. How can we collaborate with you on specific projects?",
      answer: "Please contact us using the contact form for detailed contracting and the DUA."
    }
  ];

  return (
    <div className="faq-page">
      <h1>Frequently Asked Questions</h1>
      
      <div className="faq-container">
        {faqs.map((faq, index) => (
          <div key={index} className="faq-item">
            <div 
              className="faq-question" 
              onClick={() => toggleFAQ(index)}
            >
              <h3>{faq.question}</h3>
              <span className={`faq-icon ${activeIndex === index ? 'active' : ''}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {activeIndex === index ? 
                    <line x1="5" y1="12" x2="19" y2="12"></line> :
                    <>
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </>
                  }
                </svg>
              </span>
            </div>
            
            <div className={`faq-answer ${activeIndex === index ? 'active' : ''}`}>
              {faq.answer}
            </div>
          </div>
        ))}
      </div>
      
      {showDUAModal && <DUAApplicationForm onClose={() => setShowDUAModal(false)} />}
    </div>
  );
};

// DUA Application Form Modal Component
const DUAApplicationForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    selectedDataset: '',
    projectDescription: '',
    analysisDescription: '',
    dataSafety: '',
    responsiblePI: '',
    email: '',
    responsibleIT: '',
    agreeCollaborative: false,
    agreeSpecificUse: false
  });
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  
  // Dataset options - same as search page
  const datasets = [
    { id: 'dataset1', name: 'MM IFM/DFCI 2009 RNAseq' }
  ];
  
  // Check if form is complete
  const isFormComplete = () => {
    return (
      formData.selectedDataset.trim() !== '' &&
      formData.projectDescription.trim() !== '' &&
      formData.analysisDescription.trim() !== '' &&
      formData.dataSafety.trim() !== '' &&
      formData.responsiblePI.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.responsibleIT.trim() !== '' &&
      formData.agreeCollaborative &&
      formData.agreeSpecificUse
    );
  };
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle form submission
const handleSubmit = async (e) => {
  e.preventDefault();
  
  setIsLoading(true); // Add a loading state to your component
  
  try {
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });
    
    const data = await response.json();
    
    if (data.success) {
      setFormSubmitted(true);
    } else {
      alert('There was an error submitting your request. Please try again.');
    }
  } catch (error) {
    console.error('Error:', error);
    alert('There was an error submitting your request. Please try again.');
  } finally {
    setIsLoading(false);
  }
};
  
  return (
    <div className="modal-overlay">
      <div className="dua-modal">
        <div className="modal-header">
          <h2>Data Use Agreement (DUA) Application</h2>
          <button 
            className="close-button" 
            onClick={onClose}
          >
            &times;
          </button>
        </div>
        
        {formSubmitted ? (
          <div className="success-message">
            <h3>Application Submitted Successfully</h3>
            <p>Your data access request has been sent to the review committee. If any further clarification is needed, the committee will be in touch with you.</p>
            <p>The review process can take 8 to 10 weeks.</p>
            <button 
              className="close-modal-button"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="dua-form">
            {/* Dataset Selection - Added to the top of the form */}
            <div className="form-group">
              <label htmlFor="selectedDataset">Select Dataset</label>
              <select 
                id="selectedDataset"
                name="selectedDataset"
                value={formData.selectedDataset}
                onChange={handleInputChange}
                className="dataset-dropdown"
                required
              >
                <option value="">-- Select a dataset --</option>
                {datasets.map(dataset => (
                  <option key={dataset.id} value={dataset.id}>
                    {dataset.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="projectDescription">Scientific description of your project</label>
              <textarea
                id="projectDescription"
                name="projectDescription"
                value={formData.projectDescription}
                onChange={handleInputChange}
                required
                rows="4"
              ></textarea>
            </div>
            
            <div className="form-group">
              <label htmlFor="analysisDescription">How are you going to analyze the data?</label>
              <textarea
                id="analysisDescription"
                name="analysisDescription"
                value={formData.analysisDescription}
                onChange={handleInputChange}
                required
                rows="4"
              ></textarea>
            </div>
            
            <div className="form-group">
              <label htmlFor="dataSafety">Data safety measurements in place including IT security standards</label>
              <textarea
                id="dataSafety"
                name="dataSafety"
                value={formData.dataSafety}
                onChange={handleInputChange}
                required
                rows="4"
              ></textarea>
            </div>
            
            <div className="form-group">
              <label htmlFor="responsiblePI">Responsible PI</label>
              <input
                type="text"
                id="responsiblePI"
                name="responsiblePI"
                value={formData.responsiblePI}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="responsibleIT">Responsible IT person</label>
              <input
                type="text"
                id="responsibleIT"
                name="responsibleIT"
                value={formData.responsibleIT}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group checkbox-group">
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="agreeCollaborative"
                  name="agreeCollaborative"
                  checked={formData.agreeCollaborative}
                  onChange={handleInputChange}
                  required
                />
                <label htmlFor="agreeCollaborative">I agree to collaborative research terms</label>
              </div>
            </div>
            
            <div className="form-group checkbox-group">
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="agreeSpecificUse"
                  name="agreeSpecificUse"
                  checked={formData.agreeSpecificUse}
                  onChange={handleInputChange}
                  required
                />
                <label htmlFor="agreeSpecificUse">I agree to use data only on the specific project described in the project description</label>
              </div>
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={onClose}
              >
                Cancel
              </button>



			<button 
  				type="submit" 
  				className={`submit-button ${isFormComplete() ? 'active' : 'disabled'}`}
  				disabled={!isFormComplete() || isLoading}
			>
  				{isLoading ? 'Submitting...' : 'Submit Application'}
			</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};


export default FAQ;